import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Divider, 
  Drawer,
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography, 
  useTheme, 
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ApiIcon from '@mui/icons-material/Api';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useAuth } from '../contexts/AuthContext';
import { useColorMode } from '../contexts/ColorModeContext';
import { webSocketService } from '../services/webSocketService';
import { dashboardService } from '../services/dashboardService';

const drawerWidth = 240;
const collapsedDrawerWidth = 64; // Width when drawer is collapsed

const DashboardLayout: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [systemStatus, setSystemStatus] = useState('Online');
  const [statusColor, setStatusColor] = useState('success');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationCount, setNotificationCount] = useState(3);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    {
      id: '1',
      type: 'info',
      title: 'System Alert',
      message: 'Model retraining completed successfully',
      timestamp: new Date().toISOString()
    },
    {
      id: '2',
      type: 'error',
      title: 'Critical Warning',
      message: 'API response time exceeds threshold',
      timestamp: new Date(Date.now() - 3600000).toISOString()
    },
    {
      id: '3',
      type: 'warning',
      title: 'Performance Alert',
      message: 'Accuracy declined by 5% in Premier League market',
      timestamp: new Date(Date.now() - 7200000).toISOString()
    }
  ]);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { toggleColorMode, mode } = useColorMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);
  
  useEffect(() => {
    // Connect to WebSocket for real-time system status
    webSocketService.connect().catch(console.error);
    
    // Subscribe to system status updates
    const unsubscribe = webSocketService.subscribe('system_status', (data) => {
      setSystemStatus(data.status);
      setStatusColor(getStatusColor(data.status));
    });
    
    // Subscribe to notifications
    const notificationUnsubscribe = webSocketService.subscribe('notification', (data) => {
      setNotifications(prev => [data, ...prev].slice(0, 10));
      setNotificationCount(prev => prev + 1);
    });
    
    // Initial system health check
    const checkSystemHealth = async () => {
      try {
        const health = await dashboardService.getSystemHealth();
        setSystemStatus(health.status);
        setStatusColor(getStatusColor(health.status));
      } catch (error) {
        console.error('Failed to fetch system health:', error);
        setSystemStatus('Unknown');
        setStatusColor('warning');
      }
    };
    
    checkSystemHealth();
    
    return () => {
      unsubscribe();
      notificationUnsubscribe();
    };
  }, []);
  
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'online':
      case 'healthy':
        return 'success';
      case 'degraded':
      case 'warning':
        return 'warning';
      case 'offline':
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };
  
  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };
  
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
    setNotificationCount(0);
  };
  
  const handleNotificationsClose = () => {
    setNotificationAnchorEl(null);
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'System Health', icon: <MonitorHeartIcon />, path: '/dashboard/system-health' },
    { text: 'Model Performance', icon: <AssessmentIcon />, path: '/dashboard/model-performance' },
    { text: 'API Monitoring', icon: <ApiIcon />, path: '/dashboard/api-monitoring' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/dashboard/settings' },
  ];
  
  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };
  
  const drawer = (
    <Box>
      <Toolbar />
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                backgroundColor: isCurrentPath(item.path) 
                  ? theme.palette.action.selected 
                  : 'transparent',
              }}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  color: isCurrentPath(item.path) 
                    ? theme.palette.primary.main 
                    : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{ 
                  opacity: open ? 1 : 0,
                  color: isCurrentPath(item.path) 
                    ? theme.palette.primary.main 
                    : 'inherit', 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { 
            sm: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`
          },
          ml: { 
            sm: open ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` 
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              marginRight: 2,
              display: { sm: 'none' }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {!isMobile && (
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{ marginRight: 2, display: { xs: 'none', sm: 'block' } }}
            >
              {open ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
          )}
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Football Betting System Monitor
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip 
              label={`System: ${systemStatus}`} 
              color={statusColor as 'success' | 'warning' | 'error' | 'info'} 
              size="small" 
              sx={{ marginRight: 2, display: { xs: 'none', sm: 'flex' } }}
            />
            
            <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
              <IconButton color="inherit" onClick={toggleColorMode}>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Notifications">
              <IconButton 
                color="inherit"
                onClick={handleNotificationsOpen}
              >
                <Badge badgeContent={notificationCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Tooltip title={user?.username || 'User'}>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ ml: 1 }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32,
                    bgcolor: theme.palette.primary.main 
                  }}
                >
                  {user?.username.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth 
          },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: open ? drawerWidth : collapsedDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : collapsedDrawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawer}
      </Drawer>
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3,
          width: { 
            sm: `calc(100% - ${open ? drawerWidth : collapsedDrawerWidth}px)` 
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/dashboard/profile')}>Profile</MenuItem>
        <MenuItem onClick={() => navigate('/dashboard/settings')}>Settings</MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
      
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationsClose}
        onClick={handleNotificationsClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { width: 320, maxHeight: 500 }
        }}
      >
        {notifications.map((notification) => (
          <React.Fragment key={notification.id}>
            <MenuItem>
              <Box sx={{ width: '100%' }}>
                <Typography 
                  variant="subtitle2" 
                  color={
                    notification.type === 'error' 
                      ? 'error.main' 
                      : notification.type === 'warning' 
                        ? 'warning.main' 
                        : 'primary.main'
                  }
                >
                  {notification.title}
                </Typography>
                <Typography variant="body2">{notification.message}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(notification.timestamp).toLocaleString()}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
          </React.Fragment>
        ))}
        <MenuItem sx={{ justifyContent: 'center' }}>
          <Typography variant="body2" color="primary">View All Notifications</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardLayout;
